import React, { useState, useRef, useEffect } from 'react';
import { useChatSession } from './hooks/useChatSession';
import { Message } from './Message';
import { TypingIndicator } from './TypingIndicator';
import { HistoryView } from './HistoryView';
import { ErrorBoundary } from './ErrorBoundary';
import { STARTERS, FAB_SIZE, FAB_POSITION, FAB_Z_INDEX, MOBILE_HEIGHT_VH, MOBILE_BREAKPOINT } from './chat-constants';
import { classifyError, getErrorMessage } from './chat-utils';
import './chat-widget.css';

/**
 * Main chat widget component - modular, refactored version
 */
export default function ChatWidget() {
  const {
    sessions,
    activeSessionId,
    messages,
    loading,
    error,
    sendMessage,
    loadSessions,
    loadSession,
    deleteSession,
    sendFeedback,
    startNewChat,
    clearError,
    isEmpty,
  } = useChatSession();

  const [open, setOpen] = useState(false);
  const [view, setView] = useState('chat'); // 'chat' | 'history'
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-focus input when widget opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Load sessions when history view is opened
  useEffect(() => {
    if (open && view === 'history') {
      loadSessions();
    }
  }, [open, view, loadSessions]);

  const handleSendMessage = async (text) => {
    const content = (text || input).trim();
    if (!content || loading) return;

    setInput('');
    clearError();

    try {
      await sendMessage(content, activeSessionId);
    } catch (err) {
      // Error is already set in the hook, just log for debugging
      console.error('Message send failed:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSelectSession = async (sessionId) => {
    await loadSession(sessionId);
    setView('chat');
  };

  const handleNewChat = () => {
    startNewChat();
    setInput('');
    clearError();
  };

  const handleDeleteSession = async (sessionId) => {
    await deleteSession(sessionId);
  };

  const errorType = error ? classifyError(error) : null;
  const errorMessage = errorType ? getErrorMessage(errorType) : null;

  return (
    <ErrorBoundary>
      {/* Floating action button */}
      {!open && (
        <button
          className="chat-fab"
          onClick={() => setOpen(true)}
          aria-label="Open PropAtlas Advisor"
          style={{
            position: 'fixed',
            bottom: FAB_POSITION,
            right: FAB_POSITION,
            zIndex: FAB_Z_INDEX,
            width: FAB_SIZE,
            height: FAB_SIZE,
            borderRadius: '50%',
            background: 'var(--forest)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontSize: 22,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.28)',
          }}
        >
          💬
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="chat-panel"
          style={{
            position: 'fixed',
            bottom: FAB_POSITION,
            right: FAB_POSITION,
            zIndex: FAB_Z_INDEX,
            width: 400,
            height: 600,
            borderRadius: 12,
            background: 'var(--card)',
            border: '1px solid var(--hairline)',
            boxShadow: 'var(--shadow-modal)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--hairline)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>
                Chat
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--forest)' }}>
                PropAtlas Advisor
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {activeSessionId && (
                <button
                  onClick={handleNewChat}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--hairline)',
                    padding: '6px 12px',
                    borderRadius: 4,
                    fontSize: 11,
                    cursor: 'pointer',
                  }}
                  title="Start new conversation"
                >
                  ⊕ New
                </button>
              )}
              {sessions.length > 0 && (
                <button
                  onClick={() => setView(view === 'chat' ? 'history' : 'chat')}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--hairline)',
                    padding: '6px 12px',
                    borderRadius: 4,
                    fontSize: 11,
                    cursor: 'pointer',
                  }}
                  title={view === 'chat' ? 'View history' : 'Back to chat'}
                >
                  {view === 'chat' ? '⟲ History' : '← Back'}
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 16,
                  padding: '0 4px',
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Content area */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {view === 'chat' ? (
              <>
                {/* Messages area */}
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column' }}>
                  {isEmpty && (
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 12 }}>
                      <div style={{ fontSize: 32 }}>🏠</div>
                      <div style={{ fontSize: 12, textAlign: 'center', color: 'var(--ink-3)', maxWidth: '90%' }}>
                        Ask me anything about PropAtlas or Australian property
                      </div>
                      {STARTERS.length > 0 && (
                        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                          {STARTERS.slice(0, 3).map((q, i) => (
                            <button
                              key={i}
                              onClick={() => handleSendMessage(q)}
                              style={{
                                background: 'var(--paper-2)',
                                border: '1px solid var(--hairline)',
                                padding: '8px 12px',
                                borderRadius: 6,
                                fontSize: 11,
                                cursor: 'pointer',
                                textAlign: 'left',
                                transition: 'all .15s',
                              }}
                              onMouseEnter={(e) => (e.target.style.background = 'var(--hairline)')}
                              onMouseLeave={(e) => (e.target.style.background = 'var(--paper-2)')}
                            >
                              {q}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {!isEmpty && (
                    <>
                      {messages && messages.filter(msg => msg && msg.id).map((msg) => (
                        <Message
                          key={msg.id}
                          msg={msg}
                          onSuggestedQuestion={handleSendMessage}
                          onFeedback={sendFeedback}
                        />
                      ))}
                      {loading && <TypingIndicator />}
                      <div ref={messagesEndRef} />
                    </>
                  )}

                  {/* Error display */}
                  {error && (
                    <div className="chat-error" style={{ marginTop: 12 }}>
                      <span>{errorMessage}</span>
                      <button
                        className="chat-error-retry"
                        onClick={clearError}
                      >
                        Dismiss
                      </button>
                    </div>
                  )}
                </div>

                {/* Input area */}
                <div style={{ borderTop: '1px solid var(--hairline)', padding: '12px 16px', flexShrink: 0 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Ask something…"
                      disabled={loading}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid var(--hairline)',
                        borderRadius: 6,
                        fontSize: 13,
                        background: 'var(--card)',
                        color: 'var(--ink)',
                        outline: 'none',
                      }}
                    />
                    <button
                      onClick={() => handleSendMessage()}
                      disabled={loading || !input.trim()}
                      style={{
                        background: 'var(--forest)',
                        color: '#fff',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: 6,
                        cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                        fontSize: 12,
                        fontWeight: 500,
                        opacity: loading || !input.trim() ? 0.5 : 1,
                      }}
                    >
                      {loading ? '…' : '↵'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              // History view
              <HistoryView
                sessions={sessions}
                onSelectSession={handleSelectSession}
                onDeleteSession={handleDeleteSession}
                loading={loading}
              />
            )}
          </div>
        </div>
      )}
    </ErrorBoundary>
  );
}
